import { useEffect, useState, useCallback } from 'react';

// Module-level singletons so the deferred prompt + install state are shared
// across components (Navbar button + modal) and survive re-mounts.
let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    try {
      localStorage.setItem('mr_pwa_installed', '1');
    } catch {
      // ignore
    }
    notify();
  });
}

export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: fullscreen)').matches ||
    window.navigator.standalone === true
  );
};

export const detectPlatform = () => {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isIPadOS =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  if (isIOS || isIPadOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
};

/**
 * usePWAInstall
 *  - canInstall: true when a one-tap native install is possible (deferred prompt available)
 *  - installed:  true once the app is installed / running standalone
 *  - platform:   'ios' | 'android' | 'other'
 *  - install():  triggers the native prompt; returns the user choice outcome
 */
export const usePWAInstall = () => {
  const [, force] = useState(0);
  const [platform] = useState(detectPlatform);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const onChange = () => {
      setInstalled(isStandalone());
      force((n) => n + 1);
    };
    listeners.add(onChange);
    // Re-check standalone on visibility change (fires when returning from
    // browser install flow on some platforms).
    const onVis = () => setInstalled(isStandalone());
    document.addEventListener('visibilitychange', onVis);
    return () => {
      listeners.delete(onChange);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return { outcome: 'unavailable' };
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      deferredPrompt = null;
      notify();
      return choice || { outcome: 'dismissed' };
    } catch {
      return { outcome: 'error' };
    }
  }, []);

  return {
    canInstall: !!deferredPrompt,
    installed,
    platform,
    install,
  };
};
